import React, { useContext, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import TranscriptEditorWrapper from '@src/transcript/components/TranscriptEditorWrapper';
import { useTranscriptEditorContext } from '@src/transcript/hooks/TranscriptEditorContext';
import { TranscriptEditorStore } from '@src/transcript/TranscriptEditorStore';
import SimpleLoadingSpinner from '@src/components/SimpleLoadingSpinner';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { useTranscription } from '@src/stores-v2/StoreContext';

interface TranscriptEditorContentProps {
}

/**
 * TranscriptEditorContent - The actual transcript editor component
 * This is only mounted once we have a valid storyId
 */
const TranscriptEditorContent: React.FC<TranscriptEditorContentProps> = observer(() => {
  
  const videoCreator = useVideoCreatorStore();
  const transcription = useTranscription();
  
  // Check if data is available - now just for rendering purposes, not for conditional hook calling
  const isDataLoading = !videoCreator.isInitialized || 
                      !transcription || 
                      !transcription.originalTranscription;
  
  // Use the shared TranscriptEditorContext instead of creating a new one
  const { 
    editorStore,
    isReady: contextReady,
    refreshSpeakers
  } = useTranscriptEditorContext();
  
  // Force refresh when component mounts (both on initial load and when switching tabs)
  useEffect(() => {
    // Use the refreshSpeakers function from the context
    if (editorStore && !editorStore.isLoading) {
      
      // Get the current story ID
      const storyId = editorStore.getStoryId();
      
      // Use the public getter method to access loaded speakers
      const loadedSpeakersForStoryId = editorStore.getLoadedSpeakersForStoryId();
      
      // Only refresh speakers if they haven't been loaded for this story ID yet
      if (storyId && loadedSpeakersForStoryId !== storyId) {
        console.log(`Speakers not loaded for current story (${storyId}), refreshing speakers`);
        refreshSpeakers().catch(error => {
          console.error('Error refreshing speakers:', error);
        });
      }
      
      // Force refresh waveform data if available
      if (editorStore.waveformData) {
        console.log('Ensuring waveform data is properly set');
        const currentWaveform = editorStore.waveformData;
        
        // Force re-application of waveform data to trigger refresh
        setTimeout(() => {
          editorStore.setWaveformData(currentWaveform);
        }, 50);
      }
      
      // If container exists but isn't ready, try refreshing the state
      if (editorStore.container && editorStore.container.segments.length === 0) {
        console.log('Container exists but empty, refreshing state');
        editorStore.refreshState();
      }
    }
  }, [editorStore, refreshSpeakers]);
  
  // Handle loading state after calling hooks
  if (isDataLoading) {
    return (
      <LoadingContainer>
        <SimpleLoadingSpinner text="Loading transcript data..." />
      </LoadingContainer>
    );
  }
 
  // Show loading state based on detailed status checking
  // We need to consider both the initial data loading and the editor initialization
  if (!editorStore || editorStore.isLoading) {
    return (
      <LoadingContainer>
        <SimpleLoadingSpinner text="Initializing transcript editor..." />
      </LoadingContainer>
    );
  }
  
  // If we're past initialization but the context isn't ready, show loading
  if (!contextReady || !editorStore.isReady()) {
    return (
      <LoadingContainer>
        <SimpleLoadingSpinner text="Preparing transcript data..." />
        <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '10px' }}>
          Status: {editorStore?.isReady() ? 'Editor Ready' : 'Editor Preparing'} | 
          Context: {contextReady ? 'Ready' : 'Initializing'} |
          Container: {editorStore?.container ? `${editorStore.container.segments.length} segments` : 'None'}
        </div>
      </LoadingContainer>
    );
  }

  
  // At this point editorStore must be non-null because of our previous checks
  return (
    <Container>
      <TranscriptEditorWrapper
        editorStore={editorStore as TranscriptEditorStore}
        standalone={false}
      />
    </Container>
  );
});

// Styled components
const Container = styled.div`
  width: 100%;
  height: calc(100vh - 70px); /* Adjust as needed to fit in the ContentStudio */
  overflow: hidden;
  margin-top: -15px;
  margin-bottom: 0px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  gap: 20px;
`;

const LoadingText = styled.div`
  font-size: 16px;
  color: #f3e9d7;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  padding: 20px;
  gap: 15px;
`;

const ErrorTitle = styled.h2`
  font-size: 20px;
  color: #ff6961;
  margin: 0;
`;

const ErrorText = styled.p`
  font-size: 16px;
  color: #f3e9d7;
  text-align: center;
  max-width: 500px;
`;

const ErrorNote = styled.div`
  font-size: 14px;
  color: #f3e9d7;
  text-align: center;
  max-width: 400px;
  opacity: 0.7;
  font-style: italic;
  margin-top: 10px;
`;

export default TranscriptEditorContent;